/* eslint-disable react/jsx-key */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { StatsTableBody } from 'widgets/statspro/js/components/common/stats-table/StatsTableBody';
// Import the cell renderers from the new util file
import {
    createJumperNumberCellRenderer,
    createPlayerCellRenderer,
    renderEmptyCell
} from './TableCellRenderers';
import { useGetInterchangeQuery } from 'store/modules/api/cfs-api';

/**
 * Renders the table body for stats with custom cell configurations.
 *
 * @param {object} props - Component props.
 * @param {object[]} props.rows - Rows array from react-table.
 * @param {Function} props.getTableBodyProps - Props generator function for tbody.
 * @param {Function} props.prepareRow - Function to prepare row data before render.
 * @param {string} [props.compPid] - Competition provider identifier.
 * @param {string} props.matchId - Match ID.
 * @returns {React.JSX.Element} The rendered TableBody container.
 */
export const TableBody = ({
    rows,
    getTableBodyProps,
    prepareRow,
    compPid,
    matchId
}) => {
    const compAbbr = PULSE.app.common.match.getCompAbbr(compPid);
    const isAFLW = compAbbr === 'AFLW';
    const isMainCompetition =
        PULSE.app.common.url.shouldHavePlayerURL(compAbbr);

    const { data: interchangeData } = useGetInterchangeQuery(matchId);

    // Build memoised lookup map (playerId -> benchReason)
    const benchReasonMap = useMemo(() => {
        if (!interchangeData) {
            return {};
        }

        const map = {};
        const allInterchanges = [
            ...(interchangeData.homeInterchange || []),
            ...(interchangeData.awayInterchange || [])
        ];

        allInterchanges.forEach((item) => {
            if (item?.player?.playerId) {
                map[item.player.playerId] = item.benchReason;
            }
        });

        return map;
    }, [interchangeData]);

    const customCells = useMemo(
        () => ({
            jumperNumber: createJumperNumberCellRenderer(
                isMainCompetition,
                isAFLW
            ),
            player: createPlayerCellRenderer(
                isMainCompetition,
                isAFLW,
                benchReasonMap
            ),
            empty: renderEmptyCell
        }),
        [isMainCompetition, isAFLW, benchReasonMap]
    );

    return (
        <StatsTableBody
            rows={rows}
            getTableBodyProps={getTableBodyProps}
            prepareRow={prepareRow}
            customCells={customCells}
        />
    );
};

TableBody.propTypes = {
    rows: PropTypes.array.isRequired,
    getTableBodyProps: PropTypes.func.isRequired,
    prepareRow: PropTypes.func.isRequired,
    compPid: PropTypes.string,
    matchId: PropTypes.string.isRequired
};
